import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { templatesApi, storiesApi } from '../../api/client';
import TemplateGridCard from './TemplateGridCard';
import { getCategoryGradient } from './storyUtils';
import ConfirmDialog from '../ConfirmDialog';

const TEMPLATES_PER_PAGE = 23;

export default function TemplatesTab() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [templatesPage, setTemplatesPage] = useState(1);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [templateSearch, setTemplateSearch] = useState('');
  const [confirmTemplateId, setConfirmTemplateId] = useState(null);

  useEffect(() => { loadTemplates(); }, []);

  async function loadTemplates() {
    setTemplatesLoading(true);
    try {
      const result = await templatesApi.list();
      setTemplates(result);
    } catch { toast.error('Failed to load templates'); }
    finally { setTemplatesLoading(false); }
  }

  async function createStory() {
    setCreating(true);
    try {
      const story = await storiesApi.create({ title: 'Untitled Story', publisherName: 'My Publisher' });
      toast.success('Story created!');
      navigate(`/marketing/story-builder/${story.id}/edit`);
    } catch { toast.error('Failed to create story'); setCreating(false); }
  }

  async function useTemplate(template) {
    if (creatingFromTemplate) return;
    setCreatingFromTemplate(template.id);
    try {
      const story = await templatesApi.createStory(template.id, { title: template.name });
      toast.success('Story created from template!');
      navigate(`/marketing/story-builder/${story.id}/edit`);
    } catch { toast.error('Failed to create story from template'); }
    finally { setCreatingFromTemplate(null); }
  }

  function deleteTemplate(id, e) {
    e.stopPropagation();
    setConfirmTemplateId(id);
  }

  async function doDeleteTemplate() {
    try {
      await templatesApi.delete(confirmTemplateId);
      setTemplates(prev => prev.filter(t => t.id !== confirmTemplateId));
      toast.success('Template deleted');
    } catch { toast.error('Failed to delete template'); }
    finally { setConfirmTemplateId(null); }
  }

  const filteredTemplates = templates.filter(t => {
    const matchesFilter = templateFilter === 'all' || (templateFilter === 'my' ? !t.isBuiltIn : t.isBuiltIn);
    const matchesSearch = !templateSearch || t.name.toLowerCase().includes(templateSearch.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const myTemplatesCount = templates.filter(t => !t.isBuiltIn).length;
  const builtInTemplatesCount = templates.filter(t => t.isBuiltIn).length;
  const templatesTotalPages = Math.max(1, Math.ceil(filteredTemplates.length / TEMPLATES_PER_PAGE));
  const templatesPageClamped = Math.min(templatesPage, templatesTotalPages);
  const paginatedTemplates = filteredTemplates.slice(
    (templatesPageClamped - 1) * TEMPLATES_PER_PAGE,
    templatesPageClamped * TEMPLATES_PER_PAGE
  );

  return (
    <>
      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 mx-0">
        {/* <div className="flex items-center rounded-lg p-1 gap-0.5"
          style={{ background: 'var(--t-panel)', border: '1px solid var(--t-border)' }}>
          {([
            { key: 'all', label: 'All' },
            // { key: 'my', label: 'My Templates' },
            // { key: 'built-in', label: 'Built-in' },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => { setTemplateFilter(tab.key); setTemplatesPage(1); }}
              className="px-3 py-1 rounded-md text-sm font-medium transition-all"
              style={{
                background: templateFilter === tab.key ? 'var(--brand-primary)' : 'transparent',
                color: templateFilter === tab.key ? '#fff' : 'var(--t-text2)',
              }}
            >
              {tab.label}
              {tab.key === 'my' && <span className="ml-1.5 text-xs opacity-70">{myTemplatesCount}</span>}
              {tab.key === 'built-in' && <span className="ml-1.5 text-xs opacity-70">{builtInTemplatesCount}</span>}
            </button>
          ))}
        </div> */}
        <div className="flex-1 max-w-xs">
          {/* <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-muted)' }} /> */}
          <input
            type="text"
            placeholder="Search templates…"
            value={templateSearch}
            onChange={e => { setTemplateSearch(e.target.value); setTemplatesPage(1); }}
            className="input-field pl-8"
          />
        </div>
        <div className="sm:ml-auto text-sm" style={{ color: 'var(--t-muted)' }}>
          {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template' : 'templates'}
        </div>
      </div>

      {/* Content */}
      {templatesLoading ? (
        <div className="flex items-center justify-center py-32 mx-6">
          <div className="text-center">
            <div className="w-9 h-9 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm" style={{ color: 'var(--t-muted)' }}>Loading templates...</p>
          </div>
        </div>
      ) : templates.length === 0 ? (
        <div className="flex items-center justify-center py-24 mx-0">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ background: 'var(--t-panel)' }}>
              <FileText size={32} style={{ color: 'var(--t-muted)' }} />
            </div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--t-text)' }}>No templates yet</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--t-muted)' }}>
              Save a story as a template from the editor, or built-in templates will appear here.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-8 mx-0 mb-6">
            <button
              onClick={createStory}
              disabled={creating}
              className="group flex flex-col items-center justify-center gap-2 rounded-xl transition-all duration-200"
              style={{ aspectRatio: '9/16', border: '2px dashed var(--t-border2)', color: 'var(--t-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.color = 'var(--brand-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--t-border2)'; e.currentTarget.style.color = 'var(--t-muted)'; }}
            >
              <div className="w-10 h-10 rounded-full border-2 border-current flex items-center justify-center group-hover:scale-110 transition-transform">
                {creating ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
              </div>
              <span className="text-xs font-medium">New Template</span>
            </button>

            {paginatedTemplates.map(template => (
              <TemplateGridCard
                key={template.id}
                template={template}
                onUse={useTemplate}
                onDelete={!template.isBuiltIn ? deleteTemplate : undefined}
                creatingId={creatingFromTemplate}
                getCategoryGradient={getCategoryGradient}
              />
            ))}
          </div>

          {filteredTemplates.length > TEMPLATES_PER_PAGE && (
            <div className="flex items-center justify-center gap-3 mb-6 mx-6">
              <button
                onClick={() => setTemplatesPage(p => Math.max(1, p - 1))}
                disabled={templatesPageClamped <= 1}
                style={{
                  background: '#ffffff', border: '1px solid #e2e8f0',
                  color: templatesPageClamped <= 1 ? '#cbd5e1' : '#64748b',
                  borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 500,
                  cursor: templatesPageClamped <= 1 ? 'not-allowed' : 'pointer',
                }}
              >Prev</button>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>
                Page {templatesPageClamped} of {templatesTotalPages}
              </span>
              <button
                onClick={() => setTemplatesPage(p => Math.min(templatesTotalPages, p + 1))}
                disabled={templatesPageClamped >= templatesTotalPages}
                style={{
                  background: '#ffffff', border: '1px solid #e2e8f0',
                  color: templatesPageClamped >= templatesTotalPages ? '#cbd5e1' : '#64748b',
                  borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 500,
                  cursor: templatesPageClamped >= templatesTotalPages ? 'not-allowed' : 'pointer',
                }}
              >Next</button>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={confirmTemplateId !== null}
        onClose={() => setConfirmTemplateId(null)}
        onConfirm={doDeleteTemplate}
        title="Delete template?"
        message="This template will be permanently deleted."
        confirmLabel="Delete"
      />
    </>
  );
}
